import type {AssetRequest, AssetResponse} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import type {IFormFieldV2} from 'src/types/FormV2';

export const pageTitleDict = {
  singular: 'Asset',
  plural: 'Assets',
};

export const AssetListMeta = {
  label: 'name',
};

export const AssetMeta = (): IFormFieldV2[] => {
  return [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      validation: {
        minLength: 1,
        maxLength: 500,
      },
    },
    {
      name: 'projectId',
      label: 'Project',
      type: 'project',
      description:
        'Assign this asset to a specific project, or leave as instance-wide to make it available to all projects.',
    },
    {
      name: 'active',
      label: 'Active',
      type: 'switch',
      required: true,
      switchOptions: {
        positiveText: 'Yes',
        negativeText: 'No',
      },
      description:
        'If active, this asset will be available for usage in other parts of the system.',
    },
    {
      name: 'isLeaf',
      label: 'Physical Asset',
      type: 'switch',
      required: true,
      switchOptions: {
        positiveText: 'Yes',
        negativeText: 'No',
      },
      description:
        'If enabled, this asset is treated as an object and cannot have child assets added. For example, a "Machine Group" asset is not a physical asset and may have child assets, but a specific machine would be a physical asset.',
    },
  ];
};

export const MapAssetResponseToAssetRequest = (
  data: AssetResponse
): AssetRequest => {
  return {
    id: data.id,
    name: data.name,
    active: data.active,
    parentId: data.parentId,
    isLeaf: data.isLeaf,
    projectId: data.projectId || undefined,
  };
};

export const AssetPillConfig = (
  active?: boolean,
  isLeaf?: boolean
): PillProps[] => {
  const pills: PillProps[] = [];

  switch (active) {
    case true:
      pills.push({text: 'Active', color: 'green'});
      break;
    case false:
      pills.push({text: 'Inactive', color: 'yellow'});
      break;
    default:
      break;
  }

  switch (isLeaf) {
    case true:
      pills.push({text: 'Physical Asset', color: 'blue'});
      break;
    case false:
      pills.push({text: 'Asset Group', color: 'gray'});
      break;
    default:
      break;
  }

  return pills;
};

export const assetDocumentTitle = 'Asset';
